/**
 * Platform Client Provider
 *
 * React context provider for configuring the Platform client.
 * Wrap your app with this provider to configure the client globally.
 *
 * @example
 * ```tsx
 * import { PlatformProvider } from "platform/client";
 *
 * function App() {
 *   return (
 *     <PlatformProvider baseUrl="/api/v5">
 *       <YourApp />
 *     </PlatformProvider>
 *   );
 * }
 * ```
 */

import type { ReactNode } from "react";

import { createContext, useContext, useMemo } from "react";

import type { ClientConfig } from "./types";

import { configureClient, getClientConfig } from "./types";

// ============================================================================
// Context
// ============================================================================

const PlatformContext = createContext<ClientConfig | null>(null);

// ============================================================================
// Provider Props
// ============================================================================

export interface PlatformProviderProps {
  /** API base URL (default: "/api/v5") */
  baseUrl?: string;
  /** Clerk token template name (default: "birdwatch-template") */
  tokenTemplate?: string;
  /** Children to render */
  children: ReactNode;
}

// ============================================================================
// Provider Component
// ============================================================================

/**
 * Platform client provider.
 *
 * Configures the client SDK globally. Place this at the root of your app.
 *
 * @example
 * ```tsx
 * <PlatformProvider baseUrl="/api/v5" tokenTemplate="my-template">
 *   <App />
 * </PlatformProvider>
 * ```
 */
export function PlatformProvider({
  baseUrl,
  tokenTemplate,
  children,
}: PlatformProviderProps) {
  // Build config from props and apply to the global singleton synchronously,
  // so descendants reading getClientConfig() during their first render see the
  // configured baseUrl. (Doing this in useEffect runs after children's effects,
  // causing the first request to resolve against window.origin instead.)
  const config = useMemo<ClientConfig>(() => {
    const cfg: ClientConfig = {
      baseUrl: baseUrl ?? "/api/v5",
    };
    if (tokenTemplate) {
      cfg.tokenTemplate = tokenTemplate;
    }
    configureClient(cfg);
    return cfg;
  }, [baseUrl, tokenTemplate]);

  return (
    <PlatformContext.Provider value={config}>
      {children}
    </PlatformContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Get the current platform client configuration.
 *
 * @returns The client configuration
 * @throws If used outside of PlatformProvider
 */
export function usePlatformConfig(): ClientConfig {
  const context = useContext(PlatformContext);

  // Fall back to global config if not in provider (for backwards compatibility)
  if (context === null) {
    return getClientConfig();
  }

  return context;
}
